import RAPIER from './rapier';

/** World internals used to step without Rapier's per-step handle-map re-sync (see Simulation.step). */
interface RawStepWorld {
  physicsPipeline?: { step(...args: unknown[]): void };
  gravity: unknown;
  integrationParameters: unknown;
  islands: unknown;
  broadPhase: unknown;
  narrowPhase: unknown;
  bodies: unknown;
  colliders: unknown;
  softBodies: unknown;
  impulseJoints: unknown;
  multibodyJoints: unknown;
  ccdSolver: unknown;
}
import type { Assists, CarSpec, ControlInput } from './types';
import type { SurfaceQuery } from './surfaces';
import { Vehicle, WORLD_GROUPS } from './vehicle';

/** Fixed simulation rate (Hz). Rendering interpolates between steps. */
export const SIM_HZ = 240;
export const SIM_DT = 1 / SIM_HZ;

/** What an obstacle is made of, for the sound of hitting it (ignored by the physics): 'wood' a
 * living tree trunk, 'timber' sawn wood (logs, rails, fences). */
export type ObstacleMaterial = 'wood' | 'timber' | 'rock' | 'straw' | 'concrete' | 'metal' | 'brick';

export type StaticCollider =
  | { kind: 'trimesh'; vertices: Float32Array; indices: Uint32Array; friction?: number }
  | { kind: 'heightfield'; nrows: number; ncols: number; heights: Float32Array; scale: [number, number, number]; center: [number, number, number]; friction?: number }
  | { kind: 'cylinder'; pos: [number, number, number]; halfHeight: number; radius: number; mat?: ObstacleMaterial }
  | { kind: 'cuboid'; pos: [number, number, number]; yaw: number; half: [number, number, number]; mat?: ObstacleMaterial }
  | { kind: 'ball'; pos: [number, number, number]; radius: number; mat?: ObstacleMaterial };

let rapierReady: Promise<void> | null = null;
export function initRapier(): Promise<void> {
  if (!rapierReady) rapierReady = RAPIER.init();
  return rapierReady;
}

export interface SimOptions {
  car: CarSpec;
  colliders: StaticCollider[];
  surfaces: SurfaceQuery;
  assists: Assists;
  seed?: number;
}

/**
 * Deterministic simulation container: Rapier world + one vehicle. Same inputs + same seed +
 * same runtime ⇒ bit-identical state.
 */
export class Simulation {
  readonly world: RAPIER.World;
  readonly vehicle: Vehicle;
  readonly events: RAPIER.EventQueue;
  tick = 0;
  /** Largest chassis contact force seen in the last step (N). */
  lastImpact = 0;
  /** World direction (unit, sign as Rapier reports it) of the strongest contact force in the last step. */
  readonly lastImpactDir = { x: 0, y: 0, z: 0 };
  private readonly dirTmp = { x: 0, y: 0, z: 0 };

  private constructor(opts: SimOptions) {
    this.world = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
    this.world.timestep = SIM_DT;
    this.events = new RAPIER.EventQueue(true);
    this.addStatics(opts.colliders);
    this.vehicle = new Vehicle(RAPIER, this.world, opts.car, opts.surfaces, opts.assists, opts.seed ?? 1);
  }

  static async create(opts: SimOptions): Promise<Simulation> {
    await initRapier();
    return new Simulation(opts);
  }

  get time(): number {
    return this.tick * SIM_DT;
  }

  /**
   * Static world geometry. Trimeshes and heightfields become their own colliders; primitives
   * (trees, rocks, props) are batched into compound colliders per 128 m tile — Rapier's step cost
   * grows with the number of colliders (~85 ns each per step), compounds keep it flat.
   */
  private addStatics(list: StaticCollider[]): void {
    const R = RAPIER;
    const tiles = new Map<string, { shapes: RAPIER.Shape[]; pos: RAPIER.Vector[]; rot: RAPIER.Rotation[] }>();
    const finish = (desc: RAPIER.ColliderDesc, friction: number) => {
      desc.setFriction(friction).setRestitution(0).setCollisionGroups(WORLD_GROUPS);
      this.world.createCollider(desc);
    };
    for (const c of list) {
      switch (c.kind) {
        case 'trimesh':
          finish(R.ColliderDesc.trimesh(c.vertices, c.indices), c.friction ?? 0.8);
          break;
        case 'heightfield':
          finish(R.ColliderDesc.heightfield(c.nrows, c.ncols, c.heights, { x: c.scale[0], y: c.scale[1], z: c.scale[2] }).setTranslation(c.center[0], c.center[1], c.center[2]), c.friction ?? 0.8);
          break;
        default: {
          const key = `${Math.floor(c.pos[0] / 128)},${Math.floor(c.pos[2] / 128)}`;
          let t = tiles.get(key);
          if (!t) tiles.set(key, (t = { shapes: [], pos: [], rot: [] }));
          if (c.kind === 'cylinder') {
            t.shapes.push(new R.Cylinder(c.halfHeight, c.radius));
            t.rot.push({ x: 0, y: 0, z: 0, w: 1 });
          } else if (c.kind === 'ball') {
            t.shapes.push(new R.Ball(c.radius));
            t.rot.push({ x: 0, y: 0, z: 0, w: 1 });
          } else {
            const h = c.yaw * 0.5;
            t.shapes.push(new R.Cuboid(c.half[0], c.half[1], c.half[2]));
            t.rot.push({ x: 0, y: Math.sin(h), z: 0, w: Math.cos(h) });
          }
          t.pos.push({ x: c.pos[0], y: c.pos[1], z: c.pos[2] });
        }
      }
    }
    for (const t of tiles.values()) finish(R.ColliderDesc.compound(t.shapes, t.pos, t.rot), 0.5);
  }

  step(input: ControlInput): void {
    const v = this.vehicle;
    v.impactForce = 0;
    v.step(SIM_DT, input);
    // World.step = the physics pipeline + a re-sync of Rapier's JS-side handle maps, which builds an
    // array of every collider and makes one wasm call per collider, 240 times a second. Nothing is
    // added or removed after setup here, so step the pipeline directly (identical physics). Pinned
    // Rapier 0.21.0 (§2); falls back to World.step if the internals ever differ.
    const w = this.world as unknown as RawStepWorld;
    if (w.physicsPipeline) w.physicsPipeline.step(w.gravity, w.integrationParameters, w.islands, w.broadPhase, w.narrowPhase, w.bodies, w.colliders, w.softBodies, w.impulseJoints, w.multibodyJoints, w.ccdSolver, this.events, undefined);
    else this.world.step(this.events);
    let maxF = 0;
    this.events.drainContactForceEvents((e) => {
      const f = e.totalForceMagnitude();
      if (f > maxF) {
        maxF = f;
        const d = e.maxForceDirection(this.dirTmp as RAPIER.Vector);
        this.lastImpactDir.x = d.x;
        this.lastImpactDir.y = d.y;
        this.lastImpactDir.z = d.z;
      }
    });
    this.events.drainCollisionEvents(() => {});
    this.lastImpact = maxF;
    v.noteImpact(maxF);
    this.tick++;
  }

  /** Ray cast against static world geometry only (not the car). Returns hit distance or maxDist. */
  castStatic(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): number {
    const ray = new RAPIER.Ray({ x: ox, y: oy, z: oz }, { x: dx, y: dy, z: dz });
    const hit = this.world.castRay(ray, maxDist, true, undefined, WORLD_GROUPS, undefined, this.vehicle.body);
    return hit ? hit.timeOfImpact : maxDist;
  }

  /** Hash of the full dynamic state for determinism tests. */
  stateHash(): string {
    const v = this.vehicle;
    const t = v.body.translation();
    const r = v.body.rotation();
    const lv = v.body.linvel();
    const av = v.body.angvel();
    const parts = [t.x, t.y, t.z, r.x, r.y, r.z, r.w, lv.x, lv.y, lv.z, av.x, av.y, av.z, v.drivetrain.engineOmega, v.drivetrain.boost, v.drivetrain.gear];
    for (const w of v.wheels) parts.push(w.omega, w.kappa, w.tanAlpha, w.compression);
    const buf = new Float64Array(parts);
    const u = new Uint32Array(buf.buffer);
    let h = 2166136261;
    for (let i = 0; i < u.length; i++) {
      h ^= u[i];
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
  }

  free(): void {
    this.world.free();
    this.events.free();
  }
}
